import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Wifi, WifiOff, RefreshCw, CheckCircle, AlertTriangle, Clock } from 'lucide-react';
import { syncManager, SyncStatus } from '../services/syncManager';

const StatusContainer = styled.div<{ $isVisible: boolean }>`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  transform: ${({ $isVisible }) => $isVisible ? 'translateY(0)' : 'translateY(-100px)'};
  opacity: ${({ $isVisible }) => $isVisible ? 1 : 0};
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    top: 10px;
    right: 10px;
    left: 10px;
    justify-content: center;
  }
`;

const StatusIcon = styled.div<{ $status: 'online' | 'offline' | 'syncing' | 'error' | 'success' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  
  ${({ $status }) => {
    switch ($status) {
      case 'online':
        return 'color: #10b981;';
      case 'offline':
        return 'color: #ef4444;';
      case 'syncing':
        return 'color: #3b82f6;';
      case 'error':
        return 'color: #f59e0b;';
      case 'success':
        return 'color: #10b981;';
    }
  }}
`;

const StatusText = styled.span`
  font-size: 11px;
`;

const PendingCount = styled.span`
  background: rgba(255, 255, 255, 0.2);
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
`;

const SyncStatusIndicator: React.FC = () => {
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Subscribe to sync status updates
    const unsubscribe = syncManager.subscribe((status) => {
      setSyncStatus(status);
      
      // Show indicator if there are pending actions or errors
      const shouldShow = status.pendingActions > 0 || 
                        status.syncErrors.length > 0 || 
                        status.isSyncing ||
                        !status.isOnline;
      
      setIsVisible(shouldShow);
    });

    // Get initial status
    setSyncStatus(syncManager.getSyncStatus());

    return unsubscribe;
  }, []);

  if (!syncStatus) return null;

  const getStatusInfo = () => {
    if (!syncStatus.isOnline) {
      return {
        icon: WifiOff,
        text: 'Offline',
        status: 'offline' as const
      };
    }

    if (syncStatus.isSyncing) {
      return {
        icon: RefreshCw,
        text: 'Syncing...',
        status: 'syncing' as const
      };
    }

    if (syncStatus.syncErrors.length > 0) {
      return {
        icon: AlertTriangle,
        text: 'Sync Error',
        status: 'error' as const
      };
    }

    if (syncStatus.pendingActions > 0) {
      return {
        icon: Clock,
        text: 'Pending',
        status: 'online' as const
      };
    }

    return {
      icon: CheckCircle,
      text: 'Synced',
      status: 'success' as const
    };
  };

  const statusInfo = getStatusInfo();
  const StatusIconComponent = statusInfo.icon;

  return (
    <StatusContainer $isVisible={isVisible}>
      <StatusIcon $status={statusInfo.status}>
        <StatusIconComponent 
          size={14} 
          className={statusInfo.status === 'syncing' ? 'animate-spin' : ''}
        />
      </StatusIcon>
      <StatusText>{statusInfo.text}</StatusText>
      {syncStatus.pendingActions > 0 && (
        <PendingCount>{syncStatus.pendingActions}</PendingCount>
      )}
    </StatusContainer>
  );
};

export default SyncStatusIndicator;
